// Temporary sessionStorage-backed mock for the EXR approval chain until the EXR backend exists. (GMTEC-20707)
// TODO: Replace whole file with real API calls once the EXR approval endpoints exist; delete together with the other EXR mocks. (GMTEC-20707)
import { SpecialPaymentProposalState } from '@coin/shared/core/util-enums';
import { EmployeeSlim, StatusResponse } from '@coin/shared/core/util-models';
import {
  ExceptionalRecognitionApprovalStep,
  ExceptionalRecognitionApproverType,
  ExceptionalRecognitionPaymentCategory
} from '@coin/shared/exceptional-recognitions/util';
import { ExceptionalRecognitionRecord } from '../models/exceptional-recognition-record.model';

const CHAINS_KEY = 'exr-mock-approval-chains';

const { GlobalHrBp, NMinus2, HeadOfHierarchy, CAndB, HeadOfHR, HeadOfMA } = ExceptionalRecognitionApproverType;

type ExrApprovalStepState = ExceptionalRecognitionApprovalStep['state'];

// Steps that have already been actioned. Regeneration leaves them exactly as they are.
const COMPLETED_STEP_STATES = new Set<ExrApprovalStepState>(['Approved', 'AutoApproved', 'Rejected']);

// Approvers the org tree resolves today.
const MOCK_ORG_MANAGERS: EmployeeSlim[] = [
  { id: 'emp-org-01', gid: 'Z100AAAA', firstname: 'Katrin', lastname: 'Baumann' },
  { id: 'emp-org-02', gid: 'Z100BBBB', firstname: 'Stefan', lastname: 'Roth' },
  { id: 'emp-org-03', gid: 'Z100CCCC', firstname: 'Amara', lastname: 'Diallo' },
  { id: 'emp-org-04', gid: 'Z100DDDD', firstname: 'Peter', lastname: 'Hoffmann' },
  { id: 'emp-org-05', gid: 'Z100EEEE', firstname: 'Yuki', lastname: 'Tanaka' },
  { id: 'emp-org-06', gid: 'Z100FFFF', firstname: 'Laura', lastname: 'Bianchi' }
];

// Who held those org positions when the proposals were submitted. Seeding the chain from the former holders is what
// makes a regeneration visible in the mock: the open org-derived steps move to the current managers, the completed
// ones keep the person who actually approved them.
const MOCK_FORMER_ORG_MANAGERS: EmployeeSlim[] = [
  { id: 'emp-org-91', gid: 'Z190AAAA', firstname: 'Bernd', lastname: 'Kluge' },
  { id: 'emp-org-92', gid: 'Z190BBBB', firstname: 'Renate', lastname: 'Sommer' },
  { id: 'emp-org-93', gid: 'Z190CCCC', firstname: 'Ibrahim', lastname: 'Toure' },
  { id: 'emp-org-94', gid: 'Z190DDDD', firstname: 'Gudrun', lastname: 'Vogel' },
  { id: 'emp-org-95', gid: 'Z190EEEE', firstname: 'Hiroshi', lastname: 'Mori' },
  { id: 'emp-org-96', gid: 'Z190FFFF', firstname: 'Marco', lastname: 'Conti' }
];

// The Global HR BP eligible for a place of action. The real backend re-evaluates the eligible group from the current
// RBAF assignments and keeps the whole group on the step; the mock resolves it to its current representative.
const MOCK_GLOBAL_HR_BPS: Record<string, EmployeeSlim> = {
  Germany: { id: 'emp-ghrbp-de', gid: 'Z200DE01', firstname: 'Maria', lastname: 'Weber' },
  Italy: { id: 'emp-ghrbp-it', gid: 'Z200IT01', firstname: 'Giulia', lastname: 'Ricci' },
  Brazil: { id: 'emp-ghrbp-br', gid: 'Z200BR01', firstname: 'Rafael', lastname: 'Souza' },
  China: { id: 'emp-ghrbp-cn', gid: 'Z200CN01', firstname: 'Li', lastname: 'Zhang' },
  India: { id: 'emp-ghrbp-in', gid: 'Z200IN01', firstname: 'Anil', lastname: 'Kapoor' }
};
const MOCK_FALLBACK_GLOBAL_HR_BP: EmployeeSlim = {
  id: 'emp-ghrbp-global',
  gid: 'Z200GL01',
  firstname: 'Sarah',
  lastname: 'Johnson'
};

// Regenerates the approval chains of the given proposals: open steps are re-derived from the current org tree, RBAF
// assignments and season settings, while already approved or rejected steps and the requester's deliberate approver
// picks stay untouched. The Board and CFO approvals are flags on the record rather than chain steps and are never
// cleared here; whether an unchecked CFO flag is still required follows from the record's amount against the season's
// threshold wherever it is evaluated, so it re-evaluates itself without being stored. (GMTEC-20707)
export function regenerateExrApprovalChains(
  seasonId: string,
  records: ExceptionalRecognitionRecord[],
  settings: Record<string, unknown> | null
): StatusResponse {
  const failure = records.map(record => regenerateChain(seasonId, record, settings)).find(result => !result.isSuccess);
  return failure ?? { isSuccess: true };
}

function regenerateChain(
  seasonId: string,
  record: ExceptionalRecognitionRecord,
  settings: Record<string, unknown> | null
): StatusResponse {
  const chain = ensureChain(seasonId, record, settings);
  if (!chain.some(step => step.state === 'Pending')) {
    // The real BE returns a translation key here; the mock's plain text falls through the translate pipe unchanged.
    return { isSuccess: false, message: 'The approval chain has no open steps to regenerate' };
  }

  const structure = buildChainStructure(record, settings, chain);
  // Completed steps survive unconditionally, including one whose type the category's chain no longer contains, so a
  // proposal submitted under an older chain structure never loses an approval it already collected.
  const completedOutsideStructure = chain.filter(step => isCompleted(step) && !structure.includes(step.type));

  const regenerated = [
    ...completedOutsideStructure,
    ...structure.map(type => {
      const existing = chain.find(step => step.type === type);
      return existing && (isCompleted(existing) || isManuallyPicked(type, record.category))
        ? existing
        : {
            type,
            state: 'Pending' as ExrApprovalStepState,
            current: false,
            expectedApprover: deriveApprover(type, record, settings)
          };
    })
  ];

  writeChain(seasonId, record.id, markCurrentStep(regenerated));
  return { isSuccess: true };
}

// The approval steps a proposal of this category runs through today. An existing chain is passed on regeneration so
// that the requester's own composition choices are read from it rather than rebuilt.
function buildChainStructure(
  record: ExceptionalRecognitionRecord,
  settings: Record<string, unknown> | null,
  existingChain?: ExceptionalRecognitionApprovalStep[]
): ExceptionalRecognitionApproverType[] {
  switch (record.category) {
    case ExceptionalRecognitionPaymentCategory.SignOnBonus:
      return [GlobalHrBp, HeadOfHierarchy];
    case ExceptionalRecognitionPaymentCategory.ExceptionalRecognition:
      return [GlobalHrBp, NMinus2, HeadOfHierarchy];
    default:
      return [GlobalHrBp, CAndB, HeadOfHR, ...(includesHeadOfMa(settings, existingChain) ? [HeadOfMA] : [])];
  }
}

// Whether Head of M&A takes part is the requester's choice when submitting, so regeneration keeps the chain's
// composition and only refreshes the approver behind the step. A chain built from scratch includes the step whenever
// the season configures a Head of M&A.
function includesHeadOfMa(
  settings: Record<string, unknown> | null,
  existingChain?: ExceptionalRecognitionApprovalStep[]
): boolean {
  return existingChain
    ? existingChain.some(step => step.type === HeadOfMA)
    : !!readSetting(settings, 'transformationIncentiveHeadOfMaEmployeeId');
}

// A deliberate pick by the requester rather than a derived approver, so regeneration preserves it even while the step
// is still open: for Transformation Incentive that is the manually picked C&B approver and the individually selected
// Global HR BP. The same Global HR BP step is a derived group step for the other categories.
function isManuallyPicked(
  type: ExceptionalRecognitionApproverType,
  category: ExceptionalRecognitionPaymentCategory
): boolean {
  if (category !== ExceptionalRecognitionPaymentCategory.TransformationIncentive) {
    return false;
  }
  return type === CAndB || type === GlobalHrBp;
}

function deriveApprover(
  type: ExceptionalRecognitionApproverType,
  record: ExceptionalRecognitionRecord,
  settings: Record<string, unknown> | null
): EmployeeSlim | undefined {
  switch (type) {
    case NMinus2:
    case HeadOfHierarchy:
      // Derived from the record's org code — the mapped employee's, or the one entered manually while unmapped.
      return resolveOrgApprover(record.orgCode, type, MOCK_ORG_MANAGERS);
    case GlobalHrBp:
      return MOCK_GLOBAL_HR_BPS[record.placeOfAction] ?? MOCK_FALLBACK_GLOBAL_HR_BP;
    case HeadOfHR:
      return resolveSettingsApprover(settings, 'transformationIncentiveHeadOfHrEmployeeId');
    case HeadOfMA:
      return resolveSettingsApprover(settings, 'transformationIncentiveHeadOfMaEmployeeId');
    default:
      return undefined;
  }
}

// Stands in for the org-tree lookup: the same org code always resolves to the same manager per level.
function resolveOrgApprover(
  orgCode: string,
  type: ExceptionalRecognitionApproverType,
  managers: EmployeeSlim[]
): EmployeeSlim | undefined {
  return orgCode ? managers[hashCode(`${orgCode}-${type}`) % managers.length] : undefined;
}

// Head of HR and Head of M&A are configured on the season, so they always come from the current settings.
function resolveSettingsApprover(settings: Record<string, unknown> | null, key: string): EmployeeSlim | undefined {
  const employeeId = readSetting(settings, key);
  return employeeId ? { id: employeeId } : undefined;
}

function readSetting(settings: Record<string, unknown> | null, key: string): string {
  const value = settings?.[key];
  return value ? String(value) : '';
}

function isCompleted(step: ExceptionalRecognitionApprovalStep): boolean {
  return COMPLETED_STEP_STATES.has(step.state);
}

function markCurrentStep(steps: ExceptionalRecognitionApprovalStep[]): ExceptionalRecognitionApprovalStep[] {
  const currentIndex = steps.findIndex(step => step.state === 'Pending');
  return steps.map((step, index) => ({ ...step, current: index === currentIndex }));
}

function ensureChain(
  seasonId: string,
  record: ExceptionalRecognitionRecord,
  settings: Record<string, unknown> | null
): ExceptionalRecognitionApprovalStep[] {
  const existing = readStore()[seasonId]?.[record.id];
  if (existing) {
    return existing;
  }
  const seeded = seedChain(record, settings);
  writeChain(seasonId, record.id, seeded);
  return seeded;
}

// Builds the chain a proposal was submitted with: derived from the former org holders, and progressed to match the
// proposal's state so that both completed and open steps exist to regenerate against.
function seedChain(
  record: ExceptionalRecognitionRecord,
  settings: Record<string, unknown> | null
): ExceptionalRecognitionApprovalStep[] {
  const structure = buildChainStructure(record, settings);
  const currentIndex = hashCode(record.id) % structure.length;

  return markCurrentStep(
    structure.map((type, index) => {
      const approver = seedApprover(type, record, settings);
      const state = seedStepState(record.state, index, currentIndex);
      return state === 'Pending'
        ? { type, state, current: false, expectedApprover: approver }
        : { type, state, current: false, updatedBy: approver };
    })
  );
}

function seedStepState(
  recordState: SpecialPaymentProposalState,
  index: number,
  currentIndex: number
): ExrApprovalStepState {
  if (recordState === SpecialPaymentProposalState.AutoApproved) {
    return 'AutoApproved';
  }
  if (recordState === SpecialPaymentProposalState.Approved) {
    return 'Approved';
  }
  if (index < currentIndex) {
    return 'Approved';
  }
  return index === currentIndex && recordState === SpecialPaymentProposalState.Rejected ? 'Rejected' : 'Pending';
}

// Org-derived steps were assigned to the former holders; everything else was already what it is today.
function seedApprover(
  type: ExceptionalRecognitionApproverType,
  record: ExceptionalRecognitionRecord,
  settings: Record<string, unknown> | null
): EmployeeSlim | undefined {
  if (type === NMinus2 || type === HeadOfHierarchy) {
    return resolveOrgApprover(record.orgCode, type, MOCK_FORMER_ORG_MANAGERS);
  }
  if (type === CAndB) {
    return { id: 'emp-cnb-01', gid: 'Z300CB01', firstname: 'Lisa', lastname: 'Davis' };
  }
  return deriveApprover(type, record, settings);
}

function hashCode(value: string): number {
  return [...value].reduce((hash, char) => hash + char.charCodeAt(0), 0);
}

function readStore(): Record<string, Record<string, ExceptionalRecognitionApprovalStep[]>> {
  try {
    return JSON.parse(sessionStorage.getItem(CHAINS_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function writeChain(seasonId: string, recordId: string, steps: ExceptionalRecognitionApprovalStep[]): void {
  const store = readStore();
  store[seasonId] = { ...store[seasonId], [recordId]: steps };
  sessionStorage.setItem(CHAINS_KEY, JSON.stringify(store));
}
